import React from 'react';
import { Box, Grid, Typography, Container } from '@mui/material';
import { motion } from 'framer-motion';
import { useTheme } from '../../../ThemeContext';

// Icons for Mission
import HandshakeIcon from '@mui/icons-material/Handshake';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const missionItems = [
  {
    title: 'Connect',
    desc: 'Build meaningful connections with industry professionals.',
    icon: <GroupAddIcon sx={{ fontSize: '1.5rem', color: '#07a4f5' }} />,
  },
  {
    title: 'Collaborate',
    desc: 'Work together on projects and creative ideas.',
    icon: <HandshakeIcon sx={{ fontSize: '1.5rem', color: '#07a4f5' }} />,
  },
  {
    title: 'Create',
    desc: 'Share your content and showcase your talent.',
    icon: <CameraAltIcon sx={{ fontSize: '1.5rem', color: '#07a4f5' }} />,
  },
  {
    title: 'Grow',
    desc: 'Access tools and opportunities to grow your career.',
    icon: <TrendingUpIcon sx={{ fontSize: '1.5rem', color: '#07a4f5' }} />,
  },
];

const AboutSection = () => {
  const { isDarkMode } = useTheme();

  const cardBg = isDarkMode ? '#131b2f' : '#ffffff';
  const borderColor = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
  const textPrimary = isDarkMode ? '#ffffff' : '#0d1530';
  const textSecondary = isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';

  return (
    <Box sx={{ pb: { xs: 8, md: 12 }, px: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl">
        
        {/* Mission Section */}
        <Grid container spacing={6} alignItems="center">
          
          {/* Left Cinematic Image */}
          <Grid item xs={12} lg={5}>
            <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <Box sx={{ borderRadius: '24px', overflow: 'hidden', boxShadow: isDarkMode ? '0 24px 64px rgba(0,0,0,0.5)' : '0 24px 64px rgba(7, 164, 245,0.1)', position: 'relative', width: '100%', paddingTop: '75%' }}>
                <Box component="img" src="https://images.unsplash.com/photo-1536440136628-849c177e76a1?auto=format&fit=crop&w=800&q=80" alt="Cinematic clapperboard" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                {/* Play button overlay */}
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', '&:hover': { transform: 'translate(-50%, -50%) scale(1.05)' }, transition: 'all 0.2s' }}>
                  <Box sx={{ width: 0, height: 0, borderTop: '10px solid transparent', borderBottom: '10px solid transparent', borderLeft: '16px solid #07a4f5', ml: 0.5 }} />
                </Box>
              </Box>
            </motion.div>
          </Grid>

          {/* Right Mission Content */}
          <Grid item xs={12} lg={7}>
            <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <Typography variant="h3" sx={{ fontWeight: 900, color: textPrimary, fontSize: { xs: '2rem', md: '2.5rem' }, mb: 3 }}>
                Empowering the Film Industry
              </Typography>
              <Typography variant="body1" sx={{ color: textSecondary, lineHeight: 1.8, fontSize: '1.05rem', mb: 5, maxWidth: '90%' }}>
                Filmhook is more than a platform – it's a movement to empower talent, simplify collaboration, and create endless opportunities for everyone in the film and entertainment industry.
              </Typography>

              {/* 2x2 Grid */}
              <Grid container spacing={3}>
                {missionItems.map((item, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Box sx={{ background: cardBg, p: 3, borderRadius: '16px', border: `1px solid ${borderColor}`, display: 'flex', gap: 2, alignItems: 'flex-start', height: '100%' }}>
                      <Box sx={{ background: isDarkMode ? 'rgba(7, 164, 245,0.1)' : '#f0f7ff', p: 1.5, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography sx={{ color: textPrimary, fontWeight: 800, fontSize: '1rem', mb: 1 }}>{item.title}</Typography>
                        <Typography sx={{ color: textSecondary, fontSize: '0.85rem', lineHeight: 1.6 }}>{item.desc}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

            </motion.div>
          </Grid>
          
        </Grid>
      </Container>
    </Box>
  );
};

export default AboutSection;
